import './menu.css'

import React, { useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Spinner } from '@/components/spinner'
import { useAppContext } from '@/context/app'
import { UsersReportWizardPopup } from '../communal/my/reports/usersranking/wizard/Main'
import { MachineWorkingDaysReportWizardPopup } from '../communal/my/reports/machineworkingdays/wizard/Main'
import { MachinesReportWizardPopup } from '../communal/my/reports/machinesranking/wizard/Main'

export type MenuItem = {
    url?: string
    name: string
    color?: string // optional, overrides defaultClass
    action?: (item: MenuItem) => void
}

export type MenuProps = {
    openModal: (Component: React.ComponentType<any>) => void
}

export interface MenuSection {
    group: string
    items: MenuItem[]
    icon?: string
}

// sections defaults
const generalBaseSection: MenuSection = {
    group: 'general',
    items: []
}

const mapBaseSection: MenuSection = {
    group: 'maps',
    items: []
}

const specialBaseSection: MenuSection = {
    group: 'special',
    items: []
}

const contactBaseSection: MenuSection = {
    group: 'contact',
    items: []
}

const reportsBaseSection: MenuSection = {
    group: 'report',
    items: []
}
const statisticsBaseSection: MenuSection = {
    group: 'statistics',
    items: []
}
const managementBaseSection: MenuSection = {
    group: 'management',
    items: []
}

// superadmin menu
export const superadminMenuSections = (props: MenuProps): MenuSection[] => [
    {
        ...reportsBaseSection,
        items: [
            {
                name: 'menu.report_usersranking', action: (item: MenuItem) => {
                    props.openModal((props) => UsersReportWizardPopup({
                        ...props, onDone: () => {
                            props.onClose()
                        }
                    }))
                }
            }, {
                name: 'menu.report_machinesranking', action: (item: MenuItem) => {
                    props.openModal((props) => MachinesReportWizardPopup({
                        ...props, onDone: () => {
                            props.onClose()
                        }
                    }))
                }
            }, {
                name: 'menu.report_machineworkingdays', action: (item: MenuItem) => {
                    props.openModal((props) => MachineWorkingDaysReportWizardPopup({
                        ...props, onDone: () => {
                            props.onClose()
                        }
                    }))
                }
            }
        ]
    },
    {
        ...statisticsBaseSection,
        items: [
            { url: '/communal/statistics/machines', name: 'menu.global_dashboard' },
            { url: '/communal/statistics/users', name: 'menu.global_users_statistics' },
        ]
    },
    {
        ...specialBaseSection,
        items: [
            { url: '/communal/qrscanner', name: 'menu.qrscanner' },
            { url: '/qr/generator', name: 'menu.qrGenerator' }
        ]
    },
    {
        ...mapBaseSection,
        items: [
            { url: '/communal/failures/map', name: 'menu.failures_map' },
            { url: '/communal/hit/map', name: 'menu.hit_map' },
            { url: '/communal/map', name: 'menu.map' },
            { url: '/communal/battery/map', name: 'menu.battery_map' },
            { url: '/communal/machinetype/map', name: 'menu.machinetype_map' }
        ]
    },
    {
        ...managementBaseSection,
        items: [
            { url: '/superadmin/users', name: 'menu.usersMenager' },
            { url: '/communal/cards/manager', name: 'menu.cards' },
            { url: '/superadmin/usergroups', name: 'menu.usergroups' },
            { url: '/superadmin/machines', name: 'menu.adminMachines' },
            { url: '/superadmin/machinetypes', name: 'menu.machinetypes' },
        ]
    },
    {
        ...contactBaseSection,
        items: [
            { url: '/communal/contact', name: 'menu.contact' },
            { url: '/superadmin/support/questions', name: 'menu.support_questions' }
        ]
    }
]

// definition for users
export const userMenuSections = (): MenuSection[] => [
    {
        ...generalBaseSection,
        items: [
            { url: '/communal/statistics/machines', name: 'menu.global_dashboard' },
            { url: '/communal/my/machines', name: 'menu.my_machines' }
        ]
    },
    {
        ...mapBaseSection,
        items: [
            { url: '/communal/my/failures/map', name: 'menu.failures_map' },
            { url: '/communal/my/hit/map', name: 'menu.my_hit_map' },
            { url: '/communal/my/battery/map', name: 'menu.battery_map' },
            { url: '/communal/my/map', name: 'menu.map' }
        ]
    },
    {
        ...specialBaseSection,
        items: [
            { url: '/communal/qrscanner', name: 'menu.qrscanner' }
        ]
    },
    {
        ...contactBaseSection,
        items: [
            { url: '/communal/contact', name: 'menu.contact' },
            { url: '/communal/support/wizard', name: 'menu.support_questions' }
        ]
    }
]

// definition for operator
export const operatorMenuSections = userMenuSections

// definition for coordinator
export const coordinatorMenuSections = (): MenuSection[] => [
    {
        ...specialBaseSection,
        items: [
            { url: '/communal/qrscanner', name: 'menu.qrscanner' }
        ]
    }
]

// definition for serviceman
export const servicemanMenuSections = (): MenuSection[] => [
    {
        ...generalBaseSection,
        items: [
            { url: '/communal/statistics/machines', name: 'menu.global_dashboard' },
            { url: '/communal/my/machines', name: 'menu.my_machines' }
        ]
    },
    {
        ...mapBaseSection,
        items: [
            { url: '/communal/my/failures/map', name: 'menu.failures_map' },
            { url: '/communal/my/hit/map', name: 'menu.my_hit_map' },
            { url: '/communal/my/map', name: 'menu.map' },
            { url: '/communal/machinetype/map', name: 'menu.machinetype_map' }
        ]
    },
    {
        ...specialBaseSection,
        items: [
            { url: '/communal/qrscanner', name: 'menu.qrscanner' }
        ]
    },
    {
        ...contactBaseSection,
        items: [
            { url: '/communal/contact', name: 'menu.contact' },
            { url: '/communal/support/wizard', name: 'menu.support_questions' }
        ]
    }
]

// definition for company manager
export const companyManagerMenuSections = (props: MenuProps): MenuSection[] => [
    {
        ...reportsBaseSection,
        items: [
            {
                name: 'menu.report_usersranking', action: (item: MenuItem) => {
                    props.openModal((props) => UsersReportWizardPopup({
                        ...props, onDone: () => {
                            props.onClose()
                        }
                    }))
                }
            }, {
                name: 'menu.report_machineworkingdays', action: (item: MenuItem) => {
                    props.openModal((props) => MachineWorkingDaysReportWizardPopup({
                        ...props, onDone: () => {
                            props.onClose()
                        }
                    }))
                }
            }
        ]
    },
    {
        ...statisticsBaseSection,
        items: [
            { url: '/communal/statistics/machines', name: 'menu.global_dashboard' },
            { url: '/communal/statistics/users', name: 'menu.global_users_statistics' },
        ]
    },
    {
        ...mapBaseSection,
        items: [
            { url: '/communal/my/failures/map', name: 'menu.failures_map' },
            { url: '/communal/my/hit/map', name: 'menu.my_hit_map' },
            { url: '/communal/my/battery/map', name: 'menu.battery_map' },
            { url: '/communal/my/map', name: 'menu.map' },
            { url: '/communal/machinetype/map', name: 'menu.machinetype_map' }
        ]
    },
    {
        ...specialBaseSection,
        items: [
            { url: '/communal/qrscanner', name: 'menu.qrscanner' }
        ]
    },
    {
        ...managementBaseSection,
        items: [
            { url: '/communal/cards/manager', name: 'menu.cards' },
            { url: '/companymanager/my/machines', name: 'menu.my_machines' }
        ]
    },
    {
        ...contactBaseSection,
        items: [
            { url: '/communal/contact', name: 'menu.contact' },
            { url: '/communal/support/wizard', name: 'menu.support_questions' }
        ]
    }
]

// render Menu
export const Menu = ({ menuSections }: { menuSections: MenuSection[] }) => {
    const { translates } = useAppContext()
    const navigate = useNavigate()
    //   const sectionNames = ["about", "services", "contact"];

    const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
    // const scrollTo = (name: string) => {
    //     sectionRefs.current[name]?.scrollIntoView({ behavior: "smooth" });
    // };

    const scrollTo = (name: string) => {
    const el = sectionRefs.current[name]
    const container = document.querySelector('.app-wrap')
    if (el && container) {
        const elTop = el.getBoundingClientRect().top
        const containerTop = container.getBoundingClientRect().top
        const scrollTop = container.scrollTop
        const targetScrollTop = scrollTop + (elTop - containerTop)
        
        container.scrollTo({ top: targetScrollTop, behavior: 'smooth' })
    }
    };

    if (!translates) {
        return <Spinner />
    }

    return <div className='dashboard-menu'>
        <div className='top-shourcuts'>
            {menuSections.map(section => (
                <div key={section.group} className={`dashboard-top-menu-btn dashboard-top-menu-${section.group}-btn`} onClick={() => {
                    scrollTo(section.group)
                }}>
                    <img src={`/public/img/dashboard/menu/${section.group}.svg`} />
                </div>
            ))}
        </div>
        {menuSections.map(section => (
            <div
                key={section.group}
                ref={(el) => {
                    sectionRefs.current[section.group] = el;
                }}>

                <div className='menu-section-header'>
                    <div className="menu-section-title">{translates.for(`menu_groups.${section.group}`)}</div>
                    <img src={`/public/img/dashboard/menu/${section.group}.svg`} />
                </div>
                {section.items.map(item => (
                    <a
                        key={item.url}
                        className={`dashboard-menu-btn dashboard-menu-${section.group}-btn`}
                        onClick={() => item.action ? item.action(item) : item.url && navigate(item.url)}
                    >
                        {translates.for(item.name)}
                    </a>
                ))}
            </div>
        ))}
    </div>
}

export const TopMenu: React.FC<{
    menuSections: MenuSection[]
    action: (section: MenuSection) => void
}> = ({ menuSections, action }) => {
    const { translates } = useAppContext()

    if (!translates) {
        return <Spinner />
    }

    return (
        <div className='dashboard-top-menu'>
            {menuSections.map(section => (
                // <div key={section.group} className={`dashboard-top-menu-${section.group}-btn`} onClick={() => action(section)}>
                //     <div className="menu-section-title">{translates.for(`menu_groups.${section.group}`)}</div>
                // </div>
                <div key={section.group} className={`dashboard-top-menu-btn dashboard-top-menu-${section.group}-btn`} onClick={() => action(section)}>
                    <img src={`/public/img/dashboard/menu/${section.group}.svg`} />
                </div>
            ))}
        </div>
    )
}

export const SectionMenuPopup: React.FC<{
    action: (section: MenuSection) => void
    onClose: () => void
    section: MenuSection
}> = ({ section, action, onClose }) => {
    const { translates } = useAppContext()
    const navigate = useNavigate()

    if (!translates) {
        return <Spinner />
    }

    return (
        <div className='dashboard-section-menu-popup'>
            <div className="menu-section-title">{translates.for(`menu_groups.${section.group}`)}</div>
            {section.items.map(item => (
                <a
                    key={item.url}
                    className={`dashboard-menu-btn dashboard-menu-${section.group}-btn`}
                    onClick={() => {
                        onClose()
                        if (item.action) {
                            item.action(item)
                        } else {
                            if (!item.url) {
                                console.error('item.url is required')
                                return
                            }
                            navigate(item.url)
                        }
                    }}
                >
                    {translates.for(item.name)}
                </a>
            ))}
        </div>
    )
}